import { Banner, Button, Checkbox, Form, Heading, Toggle } from '@boltenergy-be/design-system'
import dayjs from 'dayjs'
import parse from 'html-react-parser'
import { useRouter } from 'next/router'
import React, { MutableRefObject, useEffect, useRef, useState } from 'react'
import { useBeforeunload } from 'react-beforeunload'
import { Controller, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'next-i18next/pages'
import SignatureCanvas from 'react-signature-canvas'
import {
  CustomerFlows,
  DeliveryAddressFieldKeys,
  FlowAlternatives,
  InputGroupKeys,
  MeterDetailsFieldKeys,
  OverviewFieldKeys,
  PersonalDataFieldKeys,
  SimulationFieldKeys
} from 'store/customer/enums'
import { Language } from 'types/language'
import { getPricelistFromElectricityProducts, getPricelistFromGasProducts } from 'utils/helpers'
import { routes } from 'utils/routes'
import { DATE_FORMAT, MONTHS } from '../../constants'
import { RegistrationSteps } from '../../types'
import { addressToString, dateToString } from '../../utils'
import type { OverviewStepProps } from '../types'
import { getElectricityProduct, getGasProduct } from 'utils/customerFlow'
import { BOLT_EMAIL, BOLT_PHONE, FullLanguages, SessionStorageKeys } from 'constants/constants'
import DataBlock from 'features/registration/steps/overview/data-block/DataBlock'
import { CommonTrackingParams } from 'types/tracking'
import { goToStep, previousStep, selectItsmeKey, setCreditCheckRequired, updateInput } from 'store/customer/slice'
import { useLazyGetCheckIsPlentyInvestorQuery } from 'store/api/bolt-api'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { trackEvent, trackGTMEvent } from 'utils/tracking'
import { log, setLogContext, setLogUser } from 'utils/logging'
import { Criticality, Flow, Severity } from 'types/logging'
import SignatureSection from 'components/signature-section/SignatureSection'
import { OverviewFormInputs } from './types'
import {
  CONTACT_MODES,
  COUNTER_TYPES,
  ELProduct,
  METER_TYPES,
  NGProduct,
  PAYMENT_METHODS,
  SETTLEMENT_FREQUENCIES,
  SIGN_CHANNELS,
  SITUATIONS,
  ERROR_CODES
} from '@boltenergy-be/shared'
import { parseCreateCustomerData } from 'lib/bolt-backend/registrations/createCustomer.utils'
import { parseCreateContractData } from 'lib/bolt-backend/registrations/createContract.utils'
import boltBackend from 'lib/bolt-backend'
import styles from '../../Registration.module.scss'
import { getTotalSimulatedPrice } from 'utils/price'
import { generateItsmePrefilledKeysTranslationString, getTruthyValuesFromObject } from 'utils/itsme'
import { getSessionStorageItem } from 'utils/sessionStorage'
import { ItsmePrefilledKeys } from 'store/customer/types'
import Link from 'components/link/Link'
import { INTERNAL_REGISTRATION_URL_PARAMS } from 'constants/customer-flow'
import { HANDLED_ERROR_CODES } from 'features/registration/steps/overview/constants'

const Overview = ({ producer, producerOverCapacity, overrideNextStep, products }: OverviewStepProps) => {
  // REDUX STORE
  const marketingStore = useStoreSelector((store) => store.marketing)
  const { inputs, propositions, flowAlternative, extraAddress, itsme } = useStoreSelector((store) => store.customer)
  const { accountId, opportunityId } = useStoreSelector((store) => store.app)
  const hasOverwrittenEmail = useStoreSelector((store) => selectItsmeKey(store, PersonalDataFieldKeys.EMAIL, 'overwritten'))
  const dispatch = useStoreDispatch()
  const { simulation, personalData, meterDetails, billing, overview, deliveryAddress } = inputs
  const {
    confirmationFromPortalRegistration,
    signChannel,
    simulationType,
    campaignName,
    eanCodeMandatory,
    partnerID,
    identificationMethod,
    emailConfirmationFlow,
    noEanFlow,
    creditCheckConfirmationFlow
  } = marketingStore

  // RTK queries
  const [triggerGetCheckIsPlentyInvestor] = useLazyGetCheckIsPlentyInvestorQuery()

  // Local State
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(false)

  // References
  const signatureRef = useRef<SignatureCanvas>(null) as MutableRefObject<SignatureCanvas>

  // React Hook Form
  const signature = overview?.signature
  const { handleSubmit, register, setValue, unregister, watch, control } = useForm<OverviewFormInputs>({
    mode: 'onChange',
    defaultValues: {
      signChannel: signChannel?.[0]
    }
  })
  const watchSignChannel = watch('signChannel')
  const watchConsent = watch('agreesWithTermsAndConditions')

  // Next router
  const router = useRouter()
  const { locale, query } = router

  // i18n
  const { t } = useTranslation(['registration', 'common', 'commonFormFields', 'simulationCard'])

  // Constants
  const isNotToBeConfirmed = query[INTERNAL_REGISTRATION_URL_PARAMS.IS_NOT_TO_BE_CONFIRMED] === 'true'
  const isClosedWon = query[INTERNAL_REGISTRATION_URL_PARAMS.IS_CLOSED_WON] === 'true'
  const isInvalidNoEanFlow = query[INTERNAL_REGISTRATION_URL_PARAMS.IS_INVALID_NO_EAN_FLOW] === 'true'
  const isInvalidCreditCheckFlow = query[INTERNAL_REGISTRATION_URL_PARAMS.IS_INVALID_CREDIT_CHECK_FLOW] === 'true'
  const canConfirm = !isNotToBeConfirmed && !isClosedWon && !isInvalidNoEanFlow && !isInvalidCreditCheckFlow
  const month = MONTHS[Number(meterDetails.meterReadingMonth) - 1]
  const overwrittenKeys = getTruthyValuesFromObject<ItsmePrefilledKeys>(itsme?.overwritten)

  // Memo's
  const electricityPDFUrl =
    products && canConfirm
      ? `https://${getPricelistFromElectricityProducts(
          getElectricityProduct(products).details.code as ELProduct,
          products,
          inputs.personalData.isCompany,
          locale as Language
        )}?time=${dayjs().valueOf()}`
      : ''
  const gasPDFUrl =
    products && inputs.deliveryAddress.needsGas && canConfirm
      ? `https://${getPricelistFromGasProducts(
          getGasProduct(products).details.code as NGProduct,
          products,
          inputs.personalData.isCompany,
          locale as Language
        )}?time=${dayjs().valueOf()}`
      : ''

  /**
   * Prevents unload if one of the form fields is changed
   */
  useBeforeunload((e) => {
    if (canConfirm) e.preventDefault()
  })

  /**
   * Resets the CUSTOMER_ALREADY_HAS_EAN_CODES error when landing on the overview page
   */
  useEffect(() => {
    if (error === ERROR_CODES.eanAlreadyInPortfolio) {
      setError(null)
    }
  }, [])

  /**
   * Loads the image to the signature canvas if the signature changes
   */
  useEffect(() => {
    if (signature) {
      signatureRef?.current?.fromDataURL?.(signature)
      setValue('signature', signature, { shouldValidate: true })
    }
  }, [signature])

  // Manually register signature field
  useEffect(() => {
    if (watchSignChannel === SIGN_CHANNELS.signature) {
      register('signature', { required: true })
    } else {
      unregister('signature')
    }
  }, [watchSignChannel])

  /**
   * Confirms the overview & creates the customer
   */
  const confirm = async (data: OverviewFormInputs) => {
    const startTime = performance.now()
    setLoading(true)
    let requiresCreditCheck = false

    try {
      // Update the sign channel if set
      if (data.signChannel) {
        dispatch(updateInput({ group: InputGroupKeys.OVERVIEW, key: OverviewFieldKeys.CHOSEN_SIGN_CHANNEL, value: data.signChannel }))
      }

      if (
        eanCodeMandatory &&
        !meterDetails[MeterDetailsFieldKeys.ELECTRICITY].ean &&
        (deliveryAddress[DeliveryAddressFieldKeys.NEEDS_GAS] ? !meterDetails[MeterDetailsFieldKeys.GAS].ean : true)
      ) {
        throw new Error('EAN mandatory')
      }

      // Update the signature if the sign channel is set to Signature
      if (watchSignChannel === SIGN_CHANNELS.signature) {
        dispatch(updateInput({ group: InputGroupKeys.OVERVIEW, key: OverviewFieldKeys.SIGNATURE, value: data.signature }))
      } else {
        dispatch(updateInput({ group: InputGroupKeys.OVERVIEW, key: OverviewFieldKeys.SIGNATURE, value: '' }))
      }

      // Leave function if the producer is over capacity
      if (producerOverCapacity) return

      const { firstName, lastName, email } = personalData

      setLogUser({ email, name: `${firstName} ${lastName}` })

      // Extra address flow
      if (extraAddress.isExtraAddressFlow) {
        // Parse the registration data as CreateCustomer data
        const createContractData = await parseCreateContractData(producer, products)

        setLogContext('create_contract_data', createContractData)

        // Create the contract
        const { success, message, errorCode, data } = await boltBackend.createContract(createContractData)

        // Show an error if the request was rejected
        if (!success) {
          throw new Error(errorCode || message || 'createContract on registration overview was unsuccessful')
        }

        requiresCreditCheck = data.needsCreditCheck

        const totalDuration = performance.now() - startTime
        trackEvent(
          { category: 'extra_address', action: 'finish' },
          {
            partnerId: partnerID,
            [CommonTrackingParams.CREDIT_CHECK]: requiresCreditCheck,
            [CommonTrackingParams.CUSTOMER_TYPE]: personalData.isCompany ? 'professional' : 'residential',
            [CommonTrackingParams.DURATION]: totalDuration,
            [CommonTrackingParams.SELECTED_PRODUCT]: propositions.selectedProposition,
            [CommonTrackingParams.SIGN_CHANNEL]: watchSignChannel,
            [CommonTrackingParams.SIMULATION_TYPE]: simulationType
          }
        )
      } else {
        // Regular user confirm flow
        // Parse the registration data as CreateCustomer data
        const createCustomerData = await parseCreateCustomerData(producer, products)

        setLogContext('create_customer_data', createCustomerData)

        // Create the customer
        const { success, message, errorCode, data } = await boltBackend.createCustomer(createCustomerData)

        // Show an error if the request was rejected
        if (!success) {
          throw new Error(errorCode || message || 'createCustomer on registration overview was unsuccessful')
        }

        requiresCreditCheck = data.needsCreditCheck

        const totalDuration = performance.now() - startTime
        if (watchSignChannel !== SIGN_CHANNELS.confirmationMail) {
          const flow = noEanFlow
            ? 'no-ean'
            : creditCheckConfirmationFlow
              ? 'credit-check'
              : confirmationFromPortalRegistration
                ? 'portal-confirmation'
                : emailConfirmationFlow
                  ? 'email-confirmation'
                  : 'standard'

          // Track 'Subscription Finished' event @Semetis
          trackGTMEvent('subscription_finished', {
            account_id: accountId,
            birth_date: personalData[PersonalDataFieldKeys.DATE_OF_BIRTH],
            bolt_product: propositions.selectedProposition,
            costumer_type: personalData.isCompany ? 'company' : 'private',
            date: dayjs().format(DATE_FORMAT),
            duration_ms: totalDuration,
            dynamic_tariff: !!meterDetails[MeterDetailsFieldKeys.DYNAMIC_TARIFF],
            email: personalData.email,
            flow,
            has_bus_number: !!deliveryAddress[DeliveryAddressFieldKeys.ADDRESS]?.streetBox,
            instalment: getTotalSimulatedPrice(),
            postcode: deliveryAddress.address.postalCode,
            producer_name: producer?.name?.[locale],
            product_need: deliveryAddress.needsGas ? 'gaz + electricity' : 'electricity',
            settlement_frequency: meterDetails[MeterDetailsFieldKeys.SETTLEMENT_FREQUENCY] || SETTLEMENT_FREQUENCIES.yearly,
            solar_panel: meterDetails[MeterDetailsFieldKeys.HAS_SOLAR_PANELS],
            with_promo_code: !!simulation[SimulationFieldKeys.PROMO_CODE]?.code
          })

          const fromPage = getSessionStorageItem(SessionStorageKeys.FROM) || undefined
          trackEvent(
            { category: 'subscription', action: 'finish' },
            {
              [CommonTrackingParams.CAMPAIGN]: campaignName,
              [CommonTrackingParams.CREDIT_CHECK]: requiresCreditCheck,
              [CommonTrackingParams.CUSTOMER_TYPE]: personalData.isCompany ? 'professional' : 'residential',
              [CommonTrackingParams.DURATION]: totalDuration,
              [CommonTrackingParams.FLOW]: flow,
              [CommonTrackingParams.IDENTIFICATION_METHOD]: identificationMethod,
              [CommonTrackingParams.PORTAL_CONFIRMATION]: confirmationFromPortalRegistration,
              [CommonTrackingParams.SELECTED_PRODUCT]: propositions.selectedProposition,
              [CommonTrackingParams.SIMULATION_TYPE]: simulationType,
              [CommonTrackingParams.WITH_PROMO_CODE]: !!simulation[SimulationFieldKeys.PROMO_CODE]?.code,
              [CommonTrackingParams.FROM_PAGE]: fromPage
            }
          )
        } else {
          trackEvent(
            { category: 'subscription', action: 'confirm' },
            {
              [CommonTrackingParams.SIGN_CHANNEL]: watchSignChannel,
              [CommonTrackingParams.SIMULATION_TYPE]: simulationType,
              [CommonTrackingParams.PORTAL_CONFIRMATION]: confirmationFromPortalRegistration,
              [CommonTrackingParams.CUSTOMER_TYPE]: personalData.isCompany ? 'professional' : 'residential',
              [CommonTrackingParams.SELECTED_PRODUCT]: propositions.selectedProposition,
              [CommonTrackingParams.CAMPAIGN]: campaignName,
              [CommonTrackingParams.IDENTIFICATION_METHOD]: identificationMethod,
              [CommonTrackingParams.DURATION]: totalDuration
            }
          )
        }
      }

      if (!flowAlternative[FlowAlternatives.PLENTY] && propositions.showPlentyPropositions && opportunityId) {
        // Trigger the plenty investor check already (used in the next step)
        triggerGetCheckIsPlentyInvestor(opportunityId)
      }

      if (overrideNextStep) {
        // Redirect back to the
        return overrideNextStep()
      }

      dispatch(setCreditCheckRequired(requiresCreditCheck))
      dispatch(goToStep({ flow: CustomerFlows.REGISTRATION, step: RegistrationSteps.CONFIRMATION }))
    } catch (error) {
      setError(error.message ?? 'error')

      const isHandledErrorCode = HANDLED_ERROR_CODES.includes(error.message)

      log({
        error: error.message,
        identifier: `[${Flow.REGISTRATION}:createCustomer]`,
        severity: isHandledErrorCode ? Severity.WARNING : Severity.ERROR,
        criticality: isHandledErrorCode ? Criticality.LOW : Criticality.HIGH
      })
    } finally {
      setLoading(false)
    }
  }

  /**
   * Returns the back to step link based on the given step, i18n key & default value
   *
   * @param {RegistrationSteps} step
   * @returns {JSX.Element | null}
   */
  const getGoBackToStepLink = (step: RegistrationSteps) => {
    return !confirmationFromPortalRegistration ? (
      <Button representation="link" onClick={() => dispatch(goToStep({ flow: CustomerFlows.REGISTRATION, step }))}>
        {t('common:change', 'Wijzig')}
      </Button>
    ) : null
  }

  /**
   * Clears signature and removes the value from react hook form
   */
  const clearSignature = () => {
    signatureRef.current.clear()
    setValue('signature', '', { shouldValidate: true })
  }

  /**
   * Returns the error text based on the given error code.
   *
   * @param {string} error
   * @returns {string}
   */
  const getErrorText = (error: string): string => {
    if (HANDLED_ERROR_CODES.some((code) => error === code)) {
      switch (error as (typeof HANDLED_ERROR_CODES)[number]) {
        case ERROR_CODES.eanAlreadyInPortfolio:
          return t('common:errors.customerAlreadyHasEanCodes', { count: meterDetails?.gas?.ean ? 2 : 1 })

        case ERROR_CODES.requestedProducerFull:
          return t('common:errors.requestedProducerFull')

        case ERROR_CODES.badGraydonCreditCheck:
          return t('common:errors.badGraydonCreditCheck')
      }
    }

    return t('common:error')
  }

  return !canConfirm ? (
    <>
      <Heading as="h1" variant="h3" serif>
        {isInvalidNoEanFlow || isInvalidCreditCheckFlow
          ? t('overview.cantConfirm.isInvalidFlow', { context: isInvalidCreditCheckFlow ? 'creditCheck' : 'noEan' })
          : t(isClosedWon ? 'overview.cantConfirm.isClosedWon.title' : 'overview.cantConfirm.isNotToBeConfirmed.title')}
      </Heading>

      {isClosedWon && (
        <p>{parse(t('overview.cantConfirm.isClosedWon.description', { address: addressToString(deliveryAddress.address, true) }))}</p>
      )}
    </>
  ) : emailConfirmationFlow && hasOverwrittenEmail ? (
    <main className="flex-col gap-600">
      <header>
        <Heading as="h1" variant="h3" serif>
          {t('overview.itsmeEmailMismatch.pageTitle')}
        </Heading>
      </header>

      <Banner type="blocking" title={t('overview.itsmeEmailMismatch.title')}>
        <p>
          <Trans
            t={t}
            ns="registration"
            i18nKey="overview.itsmeEmailMismatch.message"
            components={[
              <Link as="a" variant="inline" className="weight-semibold" href={`mailto:${BOLT_EMAIL}`} />,
              <Link as="a" variant="inline" className="weight-semibold" href={`tel:${BOLT_PHONE}`} />
            ]}
          />
        </p>
      </Banner>
    </main>
  ) : (
    <main className="flex-col gap-600">
      <header>
        <Heading as="h1" variant="h3" serif>
          {t('overview.title')}
        </Heading>
        <p>{t('overview.description')}</p>
      </header>

      {emailConfirmationFlow && !!overwrittenKeys.length && (
        <Banner type="warning" title={t('overview.itsmeOverwrittenWarning.title')}>
          <p>
            {parse(
              t('overview.itsmeOverwrittenWarning.message', {
                fields: generateItsmePrefilledKeysTranslationString(overwrittenKeys, t).toLowerCase()
              })
            )}
          </p>
        </Banner>
      )}

      {/* DELIVERY ADDRESS */}
      <section className="flex-col gap-500">
        <header className="flex-row gap-400">
          <Heading as="h2" variant="h3" serif>
            {t('deliveryAddress.stepTitle')}
          </Heading>
          {getGoBackToStepLink(RegistrationSteps.DELIVERY_ADDRESS)}
        </header>

        <DataBlock label={t('overview.sections.deliveryAddress.connection')}>
          {t(deliveryAddress[DeliveryAddressFieldKeys.NEEDS_GAS] ? 'common:electricityAndGas' : 'common:electricity')}
        </DataBlock>

        <DataBlock label={t('overview.sections.deliveryAddress.address')}>{parse(addressToString(deliveryAddress.address))}</DataBlock>
      </section>

      {/* PERSONAL DATA */}
      <section className="flex-col gap-500">
        <header className="flex-row gap-400">
          <Heading as="h2" variant="h3" serif>
            {t('personalData.stepTitle')}
          </Heading>
          {getGoBackToStepLink(RegistrationSteps.PERSONAL_DATA)}
        </header>

        {personalData.isCompany && (
          <DataBlock label={t('overview.sections.personalData.company')}>
            <span>
              {personalData.companyName} {t(`common:legalForms.${personalData.legalForm}`)}
            </span>
            <span>
              {t('commonFormFields:companyNumber.label')}: {personalData[PersonalDataFieldKeys.COMPANY_NUMBER]}
            </span>
            {personalData[PersonalDataFieldKeys.SUBJECT_TO_VAT] && <span>{t('personalData.fields.subjectToVat.label')}</span>}
          </DataBlock>
        )}

        <DataBlock label={t('overview.sections.personalData.contact')}>
          <span>
            {personalData[PersonalDataFieldKeys.FIRSTNAME]} {personalData[PersonalDataFieldKeys.LASTNAME]}
          </span>
          <span>{personalData[PersonalDataFieldKeys.EMAIL]}</span>
          <span>{personalData[PersonalDataFieldKeys.MOBILE_PHONE]}</span>
        </DataBlock>

        {personalData.dateOfBirth && (
          <DataBlock label={t('commonFormFields:dateOfBirth.label')}>{dayjs(personalData.dateOfBirth).format(DATE_FORMAT)}</DataBlock>
        )}

        <DataBlock label={t('personalData.fields.language.label')}>{FullLanguages[personalData.language]}</DataBlock>
      </section>

      {/* METER DETAILS */}
      <section className="flex-col gap-500">
        <header className="flex-row gap-400">
          <Heading as="h2" variant="h3" serif>
            {t('meterDetails.stepTitle')}
          </Heading>
          {getGoBackToStepLink(RegistrationSteps.METER_DETAILS)}
        </header>

        {/* - My situation */}
        <DataBlock label={t('overview.sections.meterDetails.situation')}>
          {meterDetails.situation === SITUATIONS.newConnection
            ? t('meterDetails.fields.situation.options.newConnection')
            : meterDetails.situation === SITUATIONS.move
              ? t('meterDetails.fields.situation.options.move')
              : t('meterDetails.fields.situation.options.switch')}
        </DataBlock>

        <DataBlock label={t('overview.sections.meterDetails.solarPanels')}>
          {meterDetails.hasSolarPanels ? t('common:yes', 'Ja') : t('common:no', 'Nee')}
        </DataBlock>

        <DataBlock label={t('overview.sections.meterDetails.counterType')}>
          {meterDetails[MeterDetailsFieldKeys.COUNTER_TYPE] === COUNTER_TYPES.digital ? t('common:yes', 'Ja') : t('common:no', 'Nee')}
        </DataBlock>

        {/* - Previous supplier*/}
        {meterDetails[MeterDetailsFieldKeys.PREVIOUS_SUPPLIER] && (
          <DataBlock label={t('overview.sections.meterDetails.previousSupplier')}>
            {meterDetails.previousSupplier?.otherSupplier || meterDetails.previousSupplier?.supplier}
          </DataBlock>
        )}

        {/* - Meter reading month */}
        {meterDetails.situation === SITUATIONS.switch && meterDetails.meterReadingMonth && inputs.meterDetails.hasSolarPanels && (
          <DataBlock label={t('overview.sections.meterDetails.meteringMonth')}>{t(month.i18nKey, month.defaultValue)}</DataBlock>
        )}

        {/* - Electricity */}
        {meterDetails.knowsMeterDetails ? (
          <>
            <DataBlock label={t('overview.sections.meterDetails.meterType.label')}>
              {inputs.simulation.meterType === METER_TYPES.single
                ? t('overview.sections.meterDetails.meterType.single')
                : t('overview.sections.meterDetails.meterType.double')}
            </DataBlock>

            <DataBlock label={t('common:electricity', 'Elektriciteit')}>
              <span>
                {t('commonFormFields:ean.label')}: {meterDetails.electricity.ean}
              </span>
              {meterDetails.situation === SITUATIONS.move && (
                <>
                  {meterDetails.electricity.meterNumber && (
                    <span>
                      {t('commonFormFields:meterNumber.label')}: {meterDetails.electricity.meterNumber}
                    </span>
                  )}

                  {inputs.simulation.meterType === METER_TYPES.single && meterDetails.electricity.total && (
                    <span>
                      {t('commonFormFields:meterReading.label')}: {meterDetails.electricity.total}
                    </span>
                  )}

                  {inputs.simulation.meterType === METER_TYPES.double && (
                    <>
                      {meterDetails.electricity.day && (
                        <span>
                          {t('commonFormFields:meterReading.label')} {t('commonFormFields:meterReading.day')}:{' '}
                          {meterDetails.electricity.day}
                        </span>
                      )}

                      {meterDetails.electricity.night && (
                        <span>
                          {t('commonFormFields:meterReading.label')} {t('commonFormFields:meterReading.night')}:{' '}
                          {meterDetails.electricity.night}
                        </span>
                      )}
                    </>
                  )}
                </>
              )}
            </DataBlock>

            {inputs.simulation.hasExclusiveNightMeter && (
              <>
                <DataBlock>
                  <span>
                    {t('commonFormFields:meterReading.label')} {t('commonFormFields:meterReading.exclNight')}:{' '}
                    {meterDetails.electricity.exclNight}
                  </span>
                  {meterDetails.situation === SITUATIONS.move && meterDetails.electricity.exclNight && (
                    <span>
                      {t('commonFormFields:ean.label')} {t('commonFormFields:ean.exclNight')}: {meterDetails.electricity.eanExclNight}
                    </span>
                  )}
                </DataBlock>
              </>
            )}
          </>
        ) : (
          <DataBlock label={t('common:electricity', 'Elektriciteit')}>{t('overview.sections.meterDetails.dontKnow')}</DataBlock>
        )}

        {/* - Gas */}
        {inputs.deliveryAddress.needsGas && (
          <>
            {meterDetails.knowsMeterDetails ? (
              <>
                <DataBlock label={t('common:gas', 'Gas')}>
                  <span>
                    {t('commonFormFields:ean.label')}: {meterDetails.gas.ean}
                  </span>
                  {meterDetails.situation === SITUATIONS.move && (
                    <>
                      {meterDetails.gas.meterNumber && (
                        <span>
                          {t('commonFormFields:meterNumber.label')}: {meterDetails.gas.meterNumber}
                        </span>
                      )}

                      {meterDetails.gas.total && (
                        <span>
                          {t('commonFormFields:meterReading.label')}: {meterDetails.gas.total}
                        </span>
                      )}
                    </>
                  )}
                </DataBlock>
              </>
            ) : (
              <DataBlock label={t('common:gas', 'Gas')}>{t('overview.sections.meterDetails.dontKnow')}</DataBlock>
            )}
          </>
        )}

        {/* - Contract start date */}
        {meterDetails.situation !== SITUATIONS.newConnection && (
          <DataBlock label={t('overview.sections.meterDetails.contractStartDate')}>
            {dateToString(meterDetails.contractStartDate)}
          </DataBlock>
        )}

        {/* Dynamic tariff */}
        {meterDetails.settlementFrequency === SETTLEMENT_FREQUENCIES.monthly && (
          <DataBlock label={t('overview.sections.meterDetails.monthlySettlement')}>{t('common:yes', 'Ja')}</DataBlock>
        )}

        {/* Dynamic tariff */}
        {meterDetails.dynamicTariff && (
          <DataBlock label={t('overview.sections.meterDetails.dynamicTariff')}>{t('common:yes', 'Ja')}</DataBlock>
        )}
      </section>

      {/* BILLING DETAILS */}
      <section className="flex-col gap-500">
        <header className="flex-row gap-400">
          <Heading as="h2" variant="h3" serif>
            {t('billing.stepTitle')}
          </Heading>
          {getGoBackToStepLink(RegistrationSteps.BILLING)}
        </header>

        <DataBlock label={t('overview.sections.billing.deliveryMode')}>
          {billing.deliveryMode === CONTACT_MODES.email
            ? t('billing.fields.deliveryMode.options.digital')
            : t('billing.fields.deliveryMode.options.paper')}
        </DataBlock>

        <DataBlock label={t('overview.sections.billing.correspondenceAddress')}>
          {parse(addressToString(billing.sameCorrespondenceAddress ? deliveryAddress.address : billing.correspondenceAddress))}
        </DataBlock>

        <DataBlock label={t('overview.sections.billing.paymentMethod')}>
          <span>
            {billing.paymentMethod === PAYMENT_METHODS.transfer
              ? t('billing.fields.paymentMethod.options.bankTransfer')
              : t('billing.fields.paymentMethod.options.directDebit')}
          </span>
          {!!billing.iban && (
            <span>
              {t('commonFormFields:iban.label')}&nbsp;{billing.iban}
            </span>
          )}
        </DataBlock>
      </section>

      <Form onSubmit={handleSubmit(confirm)}>
        {/* TOGGLE CONFIRMATION */}
        {Array.isArray(signChannel) && signChannel.length > 1 && (
          <Toggle
            active={watchSignChannel}
            className="grid-col-full"
            id="signChannel"
            isFullwidth
            onClick={(value) => {
              setValue('signChannel', value)
            }}
            options={signChannel.map((channel) => ({ label: channel, value: channel }))}
          />
        )}

        {watchSignChannel === SIGN_CHANNELS.signature && (
          <div className="grid-col-full">
            <Controller
              {...{ control }}
              render={({ fieldState }) => (
                <SignatureSection {...{ clearSignature, signatureRef, setValue }} signatureError={!!fieldState.error} />
              )}
              name="signature"
            />
          </div>
        )}

        <p className="grid-col-full">{t('overview.legal.rights14days')}</p>

        {/* Terms & Conditions Agreement */}
        <Checkbox
          {...register('agreesWithTermsAndConditions', { required: true })}
          className="grid-col-full"
          id="agreesWithTermsAndConditions"
          label={
            inputs.deliveryAddress.needsGas ? (
              <p>
                {parse(
                  t('overview.legal.termsAndConditions.withGas', {
                    electricityLink: electricityPDFUrl,
                    gasLink: gasPDFUrl,
                    generalLink: routes(locale).terms
                  })
                )}
              </p>
            ) : (
              <p>
                {parse(
                  t('overview.legal.termsAndConditions.withoutGas', {
                    electricityLink: electricityPDFUrl,
                    generalLink: routes(locale).terms
                  })
                )}
              </p>
            )
          }
        />

        {/* ACTION BUTTONS */}
        <Form.Footer className={styles.footer}>
          <Button
            disabled={loading}
            isFullwidthMobile
            onClick={() => dispatch(previousStep({ flow: CustomerFlows.REGISTRATION }))}
            variant="secondary"
          >
            {t('buttons.previous')}
          </Button>

          {/* CONFIRMATION BUTTON */}
          <Button disabled={!!error || !watchConsent || hasOverwrittenEmail} loading={loading} isFullwidthMobile>
            {t('buttons.confirm')}
          </Button>
        </Form.Footer>
      </Form>

      {error ? <Banner type="blocking" title={getErrorText(error)} /> : loading ? <p>{t('overview.loading')}</p> : null}
    </main>
  )
}

export default Overview
